// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll as hideAllTippyInstances } from "tippy.js";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { TippyPopupStyle } from "@tps/App/ComponentExtensions/Tippy/TippyPopupExtensions";
import HeaderClickable from "@tps/App/Components/Header/HeaderClickable";
import MiniCart from "@tps/App/Components/MiniCart/MiniCart";
import TippyPopup from "@tps/App/Components/Tippy/TippyPopup";
import setCloseAutoComplete from "@tps/App/Handlers/XNG/searchAutoComplete";
import setShowDropShadow from "@tps/App/Handlers/XNG/showDropShadow";
import { CustomAppState } from "@tps/App/Store/Reducers";
import IconHeaderCartInactive from "@tps/XDL/Icons/IconHeaderCartInactive";
import { color, xClassNames, xScreen } from "@tps/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    // XNG-Changes: add getCurrentPage(state).type !== "CartPage"
    shouldLoadCart:
        !getCurrentCartState(state).value &&
        !getCurrentCartState(state).isLoading &&
        getCurrentPage(state).type !== "CartPage",
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    loadCurrentCart,
    // Xng-Changes
    setShowDropShadow,
    setCloseAutoComplete,
    setNavDrawerIsOpen,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // XNG-Changes
    linkContainer?: InjectableCss;
    cartCountText?: TypographyPresentationProps;
    tippyPopup?: TippyPopupStyle;
    signOutWrapper?: InjectableCss;
}

export const cartLinkStyles: CartLinkStyles = {
    linkContainer: {
        css: css`
            display: flex;
            align-items: center;
            &:hover > ${TypographyStyle} {
                background-color: ${color("common.ui8")};
            }
        `,
    },
    cartCountText: {
        size: 12,
        color: "common.white",
        weight: "bold",
        lineHeight: "17px",
        css: css`
            padding: 6px;
            background-color: ${color("primary")};
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            &.opened {
                background-color: ${color("common.ui8")};
            }
        `,
    },
    tippyPopup: {
        main: {
            css: css`
                .tippy-box {
                    & > .tippy-content {
                        padding: 4px 0 0 0;
                    }
                }
            `,
        },
    },
};

const iconMap = {
    cart: IconHeaderCartInactive,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: string | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    // XNG-Changes
    isTabletScreen,
    setShowDropShadow,
    setCloseAutoComplete,
    setNavDrawerIsOpen,
}) => {
    const [isPopupShown, setIsPopupShown] = useState(false);

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false, getPromotions: true });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = !totalCountDisplay;
    // const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay > 999 ? "99+" : `${totalCountDisplay}`;
    }
    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const headerClickableComponent = (
        <HeaderClickable
            href={cartUrl}
            title={translate("Cart")}
            iconProps={{ src: icon, size: 18 }}
            isOpen={isPopupShown}
            replaceIcon={!showIcon}
            data-test-selector="cartLink"
            css={css`
                ${xScreen("lte", "md")} {
                    padding: 2px;
                }

                height: 25px;
                &.opened {
                    .arrow {
                        transform: rotate(180deg);
                        color: ${color("common.ui8")};
                    }
                    .headerClickable-title {
                        color: ${color("common.ui8")};
                    }
                }
            `}
            // Xng-Changes
            onClick={() => setNavDrawerIsOpen({ navDrawerIsOpen: false })}
        >
            <StyledWrapper {...styles.linkContainer}>
                <Typography {...styles.cartCountText} className={xClassNames.append(isPopupShown && "opened")}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </StyledWrapper>
        </HeaderClickable>
    );

    if (isTabletScreen) {
        return headerClickableComponent;
    }

    return (
        <TippyPopup
            extendedStyles={styles.tippyPopup}
            content={<MiniCart isPopupShown={isPopupShown} />}
            offset={[6, 20]}
            maxWidth={442}
            placement="bottom-end"
            type="menu"
            showCloseButton={false}
            onTriggerPopupOpen={isOpen => setIsPopupShown(isOpen)}
            appendTo="parent"
            onShow={() => {
                hideAllTippyInstances();
                setShowDropShadow({ showDropShadow: true });
                const inputElement = document.getElementsByClassName("header-search-input")?.[1];
                (inputElement as any)?.blur();
            }}
            onHide={() => {
                setShowDropShadow({ showDropShadow: false });
            }}
            popperOptions={{
                strategy: "absolute",
                modifiers: [
                    {
                        name: "preventOverflow",
                        options: {
                            padding: 0,
                        },
                    },
                    {
                        name: "computeStyles",
                        options: {
                            adaptive: true,
                            roundOffsets: ({ x, y }: any) => ({
                                x,
                                y,
                            }),
                        },
                    },
                ],
            }}
            // Xng-Changes: TPS-652
            onTrigger={() => setCloseAutoComplete({ closeAutoComplete: true })}
        >
            {headerClickableComponent}
        </TippyPopup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
                // XNG-Change
                isVisible: item => false,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
                // XNG-Change
                isVisible: item => false,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
